import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Crown, Trophy, Lightning, Stairs, Sword, Fire } from "@phosphor-icons/react";

/**
 * PlayerOverview — the "Overview" tab formerly in Profile.
 *
 * Now embedded in Home so players see their per-mode stats + titles + championship
 * qualification at a glance without having to click into Settings/Profile.
 *
 * Data pull:
 *   /season/profile/me          → stats block (mode counts, titles)
 *   /championship/qualification-progress (best-effort; hidden if 404)
 */
export default function PlayerOverview() {
  const [data, setData] = useState(null);
  const [qual, setQual] = useState(null);

  useEffect(() => {
    api.get("/season/profile/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/championship/qualification-progress")
       .then((r) => setQual(r.data)).catch(() => setQual(null));
  }, []);

  if (!data) return null;
  const s = data.stats || {};

  return (
    <div className="space-y-6" data-testid="home-overview">
      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-3">Game modes</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ModeStat Icon={Lightning} label="NK Daily" played={s.daily_played ?? 0} wins={s.daily_wins ?? 0} winLabel="per-Q wins" />
          <ModeStat Icon={Stairs}    label="NK Ladder" played={s.ladder_appearances ?? 0} wins={s.ladder_wins ?? 0} winLabel="summits" />
          <ModeStat Icon={Sword}     label="NK Duel" played={(s.duel_wins ?? 0) + (s.duel_losses ?? 0)} wins={s.duel_wins ?? 0} winLabel="wins" />
          <ModeStat Icon={Fire}      label="NK Escape" played={s.survival_runs ?? 0} wins={s.survival_record ?? 0} winLabel="best streak" />
        </div>
      </section>

      {qual && <QualificationCard data={qual} />}

      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-3">Titles</p>
        <div className="grid grid-cols-2 gap-3">
          <TitleCard label="National Championships" value={s.national_titles ?? 0} Icon={Trophy} />
          <TitleCard label="International Championships" value={s.international_titles ?? 0} Icon={Crown} gold />
        </div>
      </section>
    </div>
  );
}

function ModeStat({ Icon, label, played, wins, winLabel }) {
  return (
    <div className="nk-card p-4" data-testid={`overview-mode-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon size={14} weight="duotone" className="text-nk-gold" />
        <p className="text-[11px] font-medium text-white">{label}</p>
      </div>
      <p className="font-display text-2xl font-medium text-white tabular-nums">{played}</p>
      <p className="text-[10px] text-white/45 uppercase tracking-wider mt-0.5">played</p>
      <div className="mt-2 pt-2 border-t border-white/8 flex items-baseline gap-1.5">
        <span className="font-mono text-sm text-nk-gold tabular-nums">{wins}</span>
        <span className="text-[10px] text-white/45">{winLabel}</span>
      </div>
    </div>
  );
}

function TitleCard({ label, value, Icon, gold = false }) {
  return (
    <div className={`nk-card p-5 ${gold ? "border-nk-gold/30" : ""}`} data-testid={`overview-title-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={16} weight={gold ? "fill" : "duotone"} className={`mb-2 ${gold ? "text-nk-gold" : "text-nk-gold/70"}`} />
      <p className={`font-display text-3xl font-medium tabular-nums ${gold ? "text-nk-gold" : "text-white"}`}>{value}</p>
      <p className="text-[11px] text-white/55 mt-0.5">{label}</p>
    </div>
  );
}

function QualificationCard({ data }) {
  const req = data.requirements;
  const t = req.threshold;
  const ck = req.checklist;
  const pct = Math.min(100, (t.current / t.target) * 100);

  return (
    <section className="nk-card p-5 space-y-4" data-testid="qualification-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold font-medium">Championship Qualification</p>
          <p className="text-sm text-white/70 mt-1">
            {data.qualified
              ? "✓ Qualified for the National Championship"
              : "Meet all 3 requirements to secure a spot"}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-sm text-[10px] uppercase font-mono ${
          data.qualified ? "bg-nk-gold text-nk-bg" : "border border-nk-line text-nk-mute"
        }`}>{data.qualified ? "Qualified" : "In progress"}</span>
      </div>

      <ReqRow
        label="Country Top 10"
        complete={req.top10.complete}
        detail={req.top10.rank ? `Rank #${req.top10.rank} in ${data.country}` : `Not in top ${req.top10.needed}`}
      />

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm">
            <span className={t.complete ? "text-nk-gold" : "text-white"}>{t.complete ? "✓" : "•"}</span>{" "}
            Season Score threshold
          </p>
          <p className="text-xs font-mono text-white/70">
            {t.current.toLocaleString()} / {t.target.toLocaleString()}
          </p>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${t.complete ? "bg-nk-gold" : "bg-nk-gold/50"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div>
        <ReqRow
          label="Championship Checklist"
          complete={ck.complete}
          detail={`${ck.items.filter((i) => i.complete).length} / ${ck.items.length} items complete`}
        />
        <ul className="mt-2 space-y-1.5 pl-4">
          {ck.items.map((it) => (
            <li key={it.key} className="flex items-center justify-between text-xs" data-testid={`checklist-${it.key}`}>
              <span className={it.complete ? "text-nk-gold" : "text-white/70"}>
                {it.complete ? "✓" : "○"} {it.label}
              </span>
              <span className="font-mono text-white/50">{it.current} / {it.target}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ReqRow({ label, complete, detail }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm">
        <span className={complete ? "text-nk-gold" : "text-white"}>{complete ? "✓" : "•"}</span>{" "}
        {label}
      </p>
      <p className="text-xs text-white/60">{detail}</p>
    </div>
  );
}
